import React, { useMemo, useState } from 'react';

import { Alert } from 'antd';

import styled from 'styled-components';

import { useAddFromStatement, useInvoices } from '@hooks/API/UseInvoices';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import { NwButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import AccountCodeSelector from '@components/Gui/NwForm/AccountCodeSelector';

import InvoiceItem from './InvoiceItem';

/**
 * AddToInvoiceDrawer
 *
 * Adds the selected statement rows to an existing draft invoice of the same contact.
 * Proposes only draft invoices (no number) with the same type, billing info and currency.
 *
 * data: { ContactID, Type, BillingInfoID, Currency, HasMixedCurrencies, Rows: [{ StatementID, Title }] }
 */

const Content = styled.div`
    .account-code {
        margin: 1rem 0;

        .account-code-label {
            margin-bottom: 0.25rem;
        }
    }

    .invoices-list {
        margin: 1rem 0;
        border-top: ${(props) => props.theme.DefaultBorder};
    }
`;

const isDraftInvoice = (invoice) => !invoice.Number;

const AddToInvoiceDrawer = ({ data, onClose, onRowsAdded }) => {
    const { data: invoices, isFetching } = useInvoices(data.ContactID);
    const { mutateAsync: onAddFromStatement } = useAddFromStatement();
    const configurations = useConfigurations();

    // Account codes are offered only when the chart of accounts has accounts configured
    const hasAccountCodes = useMemo(() => {
        if (!configurations || !configurations.COA) {
            return false;
        }
        const COAjs = JSON.parse(configurations.COA);
        return !!(COAjs.accounts && COAjs.accounts.length > 0);
    }, [configurations]);

    const [accountCode, setAccountCode] = useState(null);
    const [addingInvoiceID, setAddingInvoiceID] = useState(null);
    // Rows still to add: after a partial failure only the failed rows are retried
    const [pendingRows, setPendingRows] = useState(data.Rows);
    const [rowErrors, setRowErrors] = useState([]);
    // Invoice that already received some rows: the statement must reload on close and the failed rows
    // can be retried only on this invoice
    const [partialInvoiceID, setPartialInvoiceID] = useState(null);

    // The contacts invoices list also includes credit notes (TypeName 'Credit...'): matching TypeName excludes them
    const draftInvoices = useMemo(() => {
        if (!invoices) {
            return [];
        }
        return invoices.filter(
            (invoice) =>
                isDraftInvoice(invoice) &&
                invoice.TypeName === data.Type &&
                invoice.BillingInfo?.ID === data.BillingInfoID &&
                invoice.Currency === data.Currency
        );
    }, [invoices, data]);

    const handleClose = () => {
        onClose(partialInvoiceID !== null);
    };

    const handleAddRows = async (invoiceID) => {
        setAddingInvoiceID(invoiceID);
        setRowErrors([]);

        // One call per statement row: a failing row doesn't stop the others
        const failedRows = [];
        const errors = [];
        for (const row of pendingRows) {
            try {
                await onAddFromStatement({
                    data: { InvoiceID: invoiceID, StatementID: row.StatementID, AccountCode: accountCode },
                });
            } catch (error) {
                failedRows.push(row);
                errors.push({ ...row, message: error?.message || 'Unknown error' });
            }
        }
        setAddingInvoiceID(null);

        if (errors.length === 0) {
            onRowsAdded(invoiceID);
            return;
        }
        if (failedRows.length < pendingRows.length) {
            setPartialInvoiceID(invoiceID);
        }
        setPendingRows(failedRows);
        setRowErrors(errors);
    };

    const isSelectDisabled = (invoiceID) => {
        if (addingInvoiceID !== null || pendingRows.length === 0) {
            return true;
        }
        return partialInvoiceID !== null && partialInvoiceID !== invoiceID;
    };

    const renderInvoices = () => {
        if (data.HasMixedCurrencies) {
            return (
                <Alert
                    type="warning"
                    message="The selected rows have different currencies. Convert them to the default currency to add them to an existing invoice."
                />
            );
        }
        if (isFetching) {
            return <Loading text="" textBlack />;
        }
        if (draftInvoices.length === 0) {
            return <EmptyState message="No draft invoices with the same billing info and currency" />;
        }
        return (
            <>
                {hasAccountCodes && (
                    <div className="account-code">
                        <div className="account-code-label">Account #</div>
                        <AccountCodeSelector value={accountCode} onChange={setAccountCode} />
                    </div>
                )}
                <div className="invoices-list">
                    {draftInvoices.map((invoice) => (
                        <InvoiceItem
                            key={invoice.ID}
                            invoice={invoice}
                            // Only drafts are listed: there's no invoice number to show
                            hideNumber
                            actions={
                                <NwButton
                                    primary
                                    label="Select"
                                    loadingLabel="adding rows..."
                                    loading={addingInvoiceID === invoice.ID}
                                    disabled={isSelectDisabled(invoice.ID)}
                                    onClick={() => handleAddRows(invoice.ID)}
                                />
                            }
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <NwDrawer onClose={handleClose} title="Add to Invoice">
            <Content>
                <h3>Invoice Currency: {data.Currency}</h3>
                <p>{`${pendingRows.length} rows to add. Select a draft invoice:`}</p>
                {rowErrors.length > 0 && (
                    <Alert
                        style={{ margin: '1rem 0' }}
                        type="error"
                        message="Some rows could not be added to the invoice. Select the invoice again to retry them."
                        description={
                            <ul>
                                {rowErrors.map((rowError) => (
                                    <li key={rowError.StatementID}>{`${rowError.Title}: ${rowError.message}`}</li>
                                ))}
                            </ul>
                        }
                    />
                )}
                {renderInvoices()}
            </Content>
        </NwDrawer>
    );
};

export default AddToInvoiceDrawer;
